import { type ReactNode, useState, useEffect, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import {
  SidebarProvider,
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarTrigger,
  Text,
  Tooltip,
  useSidebar,
} from '@cloudflare/kumo'
import {
  House,
  WifiHigh,
  Buildings,
  Users,
  ChartLine,
  ClipboardText,
  Moon,
  Sun,
  SignOut,
  UserCircle,
  CaretDown,
  Translate,
  User,
  UserPlus,
  Check,
  Warehouse,
} from '@phosphor-icons/react'
import { useHasRole } from '../auth/useRoles'
import { useTranslation, type Locale } from '../i18n'

interface Props {
  children: ReactNode | ((props: { darkMode: boolean; onToggleDarkMode: () => void }) => ReactNode)
}

// Brand assets resolved at build time. Defaults preserve the TESSET deploy.
// For tenants without dedicated wordmark assets, leave VITE_BRAND_LOGO_*_URL
// unset and we render `<favicon /> <span>{brandName}</span>` instead.
const BRAND_NAME = import.meta.env.VITE_BRAND_NAME ?? 'TESSET HUB'
const BRAND_FAVICON_LIGHT =
  import.meta.env.VITE_BRAND_FAVICON_LIGHT_URL ?? 'https://tesset.com/brand/favicon-01.svg'
const BRAND_FAVICON_DARK =
  import.meta.env.VITE_BRAND_FAVICON_DARK_URL ?? 'https://tesset.com/brand/favicon-05.svg'
const BRAND_LOGO_LIGHT =
  import.meta.env.VITE_BRAND_LOGO_LIGHT_URL ?? 'https://tesset.com/brand/logo-primary.svg'
const BRAND_LOGO_DARK =
  import.meta.env.VITE_BRAND_LOGO_DARK_URL ?? 'https://tesset.com/brand/logo-dark.svg'
const BRAND_HAS_LOGO =
  Boolean(import.meta.env.VITE_BRAND_LOGO_LIGHT_URL || import.meta.env.VITE_BRAND_LOGO_DARK_URL) ||
  !import.meta.env.VITE_BRAND_NAME // default TESSET path keeps wordmark image

function SidebarLogo() {
  const { state } = useSidebar()
  const root = document.documentElement.closest('[data-mode]') as HTMLElement | null
  const isDark = root?.getAttribute('data-mode') === 'dark'
  const collapsed = state === 'collapsed'
  const favicon = isDark ? BRAND_FAVICON_DARK : BRAND_FAVICON_LIGHT

  // logo.dev favicons render as transparent PNGs that bleed into the sidebar
  // background. Tenants without dedicated wordmark assets get a white circular
  // backdrop so the brand mark stays crisp on dark sidebars.
  const faviconClass = BRAND_HAS_LOGO ? 'h-6 w-6' : 'h-6 w-6 rounded-full bg-white'

  if (collapsed) {
    return <img src={favicon} alt={BRAND_NAME} className={faviconClass} />
  }

  if (BRAND_HAS_LOGO) {
    return (
      <img src={isDark ? BRAND_LOGO_DARK : BRAND_LOGO_LIGHT} alt={BRAND_NAME} className="h-8" />
    )
  }

  return (
    <div className="flex items-center gap-2">
      <img src={favicon} alt="" className={faviconClass} />
      <span className="text-base font-semibold tracking-tight">{BRAND_NAME}</span>
    </div>
  )
}

function KeyboardShortcuts() {
  const { toggleSidebar } = useSidebar()

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
        e.preventDefault()
        toggleSidebar()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [toggleSidebar])

  return null
}

interface UserDropdownProps {
  darkMode: boolean
  onToggleDarkMode: () => void
}

function UserDropdown({ darkMode, onToggleDarkMode }: UserDropdownProps) {
  const { logout, user } = useAuth0()
  const { t, locale, setLocale } = useTranslation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open])

  useEffect(() => {
    function handleEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    if (open) {
      document.addEventListener('keydown', handleEsc)
      return () => document.removeEventListener('keydown', handleEsc)
    }
  }, [open])

  return (
    <div ref={ref} className="relative" data-testid="user-dropdown">
      <button
        data-testid="user-dropdown-trigger"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-kumo-recessed text-sm cursor-pointer"
      >
        <UserCircle size={20} className="text-kumo-subtle" />
        <span className="text-kumo-default">{user?.name ?? user?.email}</span>
        <CaretDown size={14} className="text-kumo-subtle" />
      </button>

      {open && (
        <div
          data-testid="user-dropdown-content"
          className="absolute right-0 top-full mt-1 w-56 rounded-md border border-kumo-line bg-kumo-elevated shadow-lg z-50"
        >
          <div className="px-3 py-2 border-b border-kumo-line">
            <Text as="p" size="sm" DANGEROUS_className="font-medium">
              {user?.name}
            </Text>
            <Text as="p" variant="secondary" size="xs">
              {user?.email}
            </Text>
          </div>

          <div className="py-1">
            <button
              data-testid="dropdown-profile"
              onClick={() => {
                navigate('/profile')
                setOpen(false)
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-kumo-recessed text-left"
            >
              <User size={16} className="text-kumo-subtle" />
              {t('nav.profile')}
            </button>

            <div className="px-3 py-1.5">
              <Text as="p" variant="secondary" size="xs" DANGEROUS_className="font-medium mb-1">
                {t('profile.language')}
              </Text>
              <div className="flex flex-col">
                <button
                  data-testid="dropdown-lang-ptbr"
                  onClick={() => {
                    setLocale('pt-BR')
                    setOpen(false)
                  }}
                  className="flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-kumo-recessed text-left"
                >
                  <Check
                    size={14}
                    className={locale === 'pt-BR' ? 'text-kumo-default' : 'invisible'}
                  />
                  Portugues (Brasil)
                </button>
                <button
                  data-testid="dropdown-lang-en"
                  onClick={() => {
                    setLocale('en')
                    setOpen(false)
                  }}
                  className="flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-kumo-recessed text-left"
                >
                  <Check
                    size={14}
                    className={locale === 'en' ? 'text-kumo-default' : 'invisible'}
                  />
                  English
                </button>
              </div>
            </div>

            <button
              data-testid="dropdown-darkmode"
              onClick={() => {
                onToggleDarkMode()
                setOpen(false)
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-kumo-recessed text-left"
            >
              {darkMode ? (
                <Sun size={16} className="text-kumo-subtle" />
              ) : (
                <Moon size={16} className="text-kumo-subtle" />
              )}
              {t('nav.darkMode')}
            </button>
          </div>

          <div className="border-t border-kumo-line py-1">
            <button
              data-testid="dropdown-logout"
              onClick={() => {
                logout({ logoutParams: { returnTo: window.location.origin } })
                setOpen(false)
              }}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 text-left"
            >
              <SignOut size={16} />
              {t('nav.logout')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export function Layout({ children }: Props) {
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('hub-wifi-dark-mode') === 'true'
  })
  const isAdmin = useHasRole('admin')
  const isAdminOrOperator = useHasRole('admin', 'operator')
  const isUser = useHasRole('user')
  const { t } = useTranslation()
  const location = useLocation()

  const toggleDarkMode = () => {
    const next = !darkMode
    localStorage.setItem('hub-wifi-dark-mode', String(next))
    setDarkMode(next)
  }

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path)

  const navItems = [
    { to: '/', label: t('nav.home'), icon: House, show: true },
    { to: '/wifi', label: t('nav.wifi'), icon: WifiHigh, show: true },
    {
      to: '/guest-invitations',
      label: t('nav.guestInvitations'),
      icon: UserPlus,
      show: isUser || isAdminOrOperator,
    },
    { to: '/companies', label: t('nav.companies'), icon: Buildings, show: isAdminOrOperator },
    { to: '/coworkers', label: t('nav.coworkers'), icon: Users, show: isAdminOrOperator },
    { to: '/wifi-networks', label: t('nav.wifiNetworks'), icon: WifiHigh, show: isAdmin },
    { to: '/buildings', label: t('nav.buildings'), icon: Warehouse, show: isAdmin },
    { to: '/wifi-monitor', label: t('nav.wifiMonitor'), icon: ChartLine, show: isAdminOrOperator },
    { to: '/audit-log', label: t('nav.auditLog'), icon: ClipboardText, show: isAdmin },
  ]

  const renderedChildren =
    typeof children === 'function'
      ? children({ darkMode, onToggleDarkMode: toggleDarkMode })
      : children

  return (
    <div
      data-mode={darkMode ? 'dark' : 'light'}
      className="bg-kumo-base text-kumo-default h-screen flex"
    >
      <SidebarProvider defaultOpen collapsible="icon" defaultWidth={240} minWidth={180}>
        <KeyboardShortcuts />
        <Sidebar
          data-testid="sidebar"
          className="border-r border-kumo-line"
          style={{ height: '100vh' }}
        >
          <SidebarHeader
            data-testid="sidebar-header"
            className="flex items-center px-3 border-b border-kumo-line"
            style={{ height: 58, minHeight: 58, maxHeight: 58 }}
          >
            <SidebarLogo />
          </SidebarHeader>

          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  {navItems
                    .filter((item) => item.show)
                    .map((item) => (
                      <SidebarMenuItem key={item.to}>
                        <Link to={item.to} style={{ textDecoration: 'none' }}>
                          <SidebarMenuButton
                            active={isActive(item.to)}
                            tooltip={item.label}
                            icon={<item.icon size={18} />}
                          >
                            {item.label}
                          </SidebarMenuButton>
                        </Link>
                      </SidebarMenuItem>
                    ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>

          <SidebarFooter>
            <SidebarMenu>
              <SidebarMenuItem>
                <Tooltip content="Collapse sidebar (Ctrl+B)" side="right">
                  <SidebarTrigger />
                </Tooltip>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarFooter>
        </Sidebar>

        <div className="flex-1 flex flex-col h-screen overflow-hidden">
          <header
            data-testid="main-header"
            className="flex items-center gap-3 px-4 border-b border-kumo-line bg-kumo-elevated shrink-0"
            style={{ height: 58, minHeight: 58, maxHeight: 58 }}
          >
            <div className="ml-auto flex items-center gap-2">
              <UserDropdown darkMode={darkMode} onToggleDarkMode={toggleDarkMode} />
            </div>
          </header>
          <main className="flex-1 overflow-y-auto">
            <div className="max-w-5xl mx-auto p-6">{renderedChildren}</div>
          </main>
        </div>
      </SidebarProvider>
    </div>
  )
}
